import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Table, Tag } from 'antd';
import useEstimateStore from '@store/useEstimateStore';
import { CycleStatus, StatusColor } from '@/constants/status';
import i18n from '@/i18n';
import { t } from '@/utils/translator';
import AttachmentCell from '@/components/user/site-details/AttachmentCell';
import CycleActionCell from '@/components/user/site-details/CycleActionCell';
import FeedbackTable from '@/components/user/site-details/FeedbackTable';

export default function EstimateCycleTable2() {
  const { EstimateDocuments, EstimateCycles, fetchEstimateCycles } = useEstimateStore();

  const [AttachmentColumnWidth, setAttachmentColumnWidth] = useState(0);
  const [RequiredDocuments, setRequiredDocuments] = useState([]);
  const [OptionalDocuments, setOptionalDocuments] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    if (EstimateDocuments && EstimateDocuments.length) {
      setRequiredDocuments(EstimateDocuments.filter(({ is_required }) => is_required === true));
      setOptionalDocuments(EstimateDocuments.filter(({ is_required }) => is_required === false));
    }
  }, [EstimateDocuments]);

  useEffect(() => {
    const _AttachmentColumnWidth =
      RequiredDocuments.length + OptionalDocuments.length
        ? `${70 / (RequiredDocuments.length + OptionalDocuments.length)}%`
        : !OptionalDocuments.length
          ? `${70 / RequiredDocuments.length}%`
          : `${70 / OptionalDocuments.length}%`;

    setAttachmentColumnWidth(_AttachmentColumnWidth);
  }, [RequiredDocuments, OptionalDocuments, setAttachmentColumnWidth]);

  const hasAnyFeedback = (cycle) => {
    const hasCycleFeedback = cycle.cycle_feedback_comment !== null;
    const hasAttachmentFeedback = Object.values(cycle.attachments).some((attachmentList) =>
      attachmentList.some((item) => item.feedback_id !== null)
    );

    return hasCycleFeedback || hasAttachmentFeedback;
  };

  return (
    <Table
      dataSource={EstimateCycles}
      rowKey="cycle_index"
      pagination={{ position: ['none', 'none'] }}
      bordered
      style={{ width: '100%' }}
      tableLayout="fixed"
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: setExpandedRowKeys,
        expandedRowRender: (record) => {
          const feedbackRows = (Object.values(record.attachments).flat() || [])
            .filter((attachment) => attachment?.feedback_id && attachment?.feedback_comment)
            .map((attachment) => ({
              key: attachment?.feedback_id,
              attachment_type: attachment?.[`document_type_name_${i18n.language}`],
              filename: attachment?.filename || '-',
              feedback: attachment?.feedback_comment,
            }));

          return <FeedbackTable row={record} feedbacks={feedbackRows} />;
        },
        rowExpandable: (record) => hasAnyFeedback(record),
        showExpandColumn: true,
      }}
    >
      <Table.Column
        title={<span className={clsx('fw-medium', 'fs-6')}>{t('회차')}</span>}
        dataIndex="cycle_index"
        key="cycle_index"
        align="center"
        width={'10%'}
        render={(cycle_index) => <span style={{ fontSize: '15px' }}>{cycle_index}</span>}
      />
      <Table.Column
        title={<span className={clsx('fw-medium', 'fs-6')}>{t('상태')}</span>}
        dataIndex="cycle_status"
        key="cycle_status"
        align="center"
        width={'10%'}
        render={(cycle_status, record) => (
          <Tag color={StatusColor[record.cycle_status]} key={cycle_status}>
            {t(CycleStatus[cycle_status])}
          </Tag>
        )}
      />
      {RequiredDocuments.length > 0 && (
        <Table.ColumnGroup
          title={<span className={clsx('fw-medium', 'fs-6')}>{t('필수 증빙서류')}</span>}
          className="text-center"
        >
          {RequiredDocuments.map((document) => (
            <Table.Column
              key={document?.document_type?.type_code}
              title={
                <span className={clsx('fw-medium', '')}>
                  {document['document_type'][`type_name_${i18n.language}`]}
                </span>
              }
              dataIndex={document?.document_type?.type_code}
              width={AttachmentColumnWidth}
              align="center"
              ellipsis
              render={(_, record) => {
                return (
                  <AttachmentCell
                    record={record}
                    documentType={document?.document_type?.type_code}
                    attachments={record.attachments[document?.document_type?.type_code]}
                    refreshCycleData={fetchEstimateCycles}
                  />
                );
              }}
            ></Table.Column>
          ))}
        </Table.ColumnGroup>
      )}
      {OptionalDocuments.length > 0 && (
        <Table.ColumnGroup
          title={<span className={clsx('fw-medium', 'fs-6')}>{t('증빙서류')}</span>}
          className="text-center"
        >
          {OptionalDocuments.map((document) => (
            <Table.Column
              key={document?.document_type?.type_code}
              title={
                <span className={clsx('fw-medium', '')}>
                  {document['document_type'][`type_name_${i18n.language}`]}
                </span>
              }
              dataIndex={document?.document_type?.type_code}
              width={AttachmentColumnWidth}
              align="center"
              ellipsis
              render={(_, record) => {
                return (
                  <AttachmentCell
                    record={record}
                    documentType={document?.document_type?.type_code}
                    attachments={record.attachments[document?.document_type?.type_code]}
                    refreshCycleData={fetchEstimateCycles}
                  />
                );
              }}
            ></Table.Column>
          ))}
        </Table.ColumnGroup>
      )}

      <Table.Column
        title={<span className={clsx('fw-medium', 'fs-6')}>{t('제출하기')}</span>}
        dataIndex="action"
        key="action"
        align="center"
        render={(_, record) => (
          <CycleActionCell
            record={record}
            onExpandRow={setExpandedRowKeys}
            RequiredDocuments={RequiredDocuments}
            OptionalDocuments={OptionalDocuments}
            hasAnyFeedback={hasAnyFeedback}
            refreshCycleData={fetchEstimateCycles}
          />
        )}
        width={'10%'}
      />
    </Table>
  );
}
